import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PrepTourAnchorId } from './tour.types';

interface AnchorEntry {
  el: HTMLElement;
  padding: number;
}

/**
 * Anchor id → live element map plus a single geometry "tick" the outlet
 * re-measures on.
 *
 * Measuring is centralised: one `ResizeObserver` (all registered elements +
 * `document.documentElement`), one capture-phase scroll listener and one
 * window resize listener, coalesced into a single `requestAnimationFrame`
 * emission of `changes$`. Observation only runs while something `watch()`es —
 * the outlet does, for the lifetime of an active guide — so an idle host pays
 * nothing. Listeners run outside the Angular zone; the outlet decides when to
 * re-enter it.
 */
@Injectable({ providedIn: 'root' })
export class PrepTourAnchorRegistry {
  private readonly entries = new Map<string, AnchorEntry>();
  private readonly _changes = new Subject<void>();
  /** Emits (outside the Angular zone) on register/unregister and on any geometry change while watched. */
  readonly changes$: Observable<void> = this._changes.asObservable();

  private watchers = 0;
  private observer: ResizeObserver | null = null;
  private frame = 0;
  private readonly onScroll = (): void => this.schedule();

  constructor(private readonly zone: NgZone) {}

  register(id: PrepTourAnchorId, el: HTMLElement, padding = 6): () => void {
    this.entries.set(id, { el, padding });
    if (this.observer) this.observer.observe(el);
    this.emit();
    return () => {
      const current = this.entries.get(id);
      if (!current || current.el !== el) return;
      this.entries.delete(id);
      if (this.observer) this.observer.unobserve(el);
      this.emit();
    };
  }

  has(id: PrepTourAnchorId): boolean {
    return this.entries.has(id);
  }

  resolve(id: PrepTourAnchorId): HTMLElement | null {
    const entry = this.entries.get(id);
    return entry ? entry.el : null;
  }

  ids(): string[] {
    return Array.from(this.entries.keys());
  }

  /** Viewport rect of an anchor (padding applied), or null while it is not registered. */
  rectOf(id: PrepTourAnchorId | undefined): DOMRect | null {
    if (!id) return null;
    const entry = this.entries.get(id);
    if (!entry) return null;
    const r = entry.el.getBoundingClientRect();
    const p = entry.padding;
    return new DOMRect(r.left - p, r.top - p, r.width + 2 * p, r.height + 2 * p);
  }

  /** Scroll the anchor into view (no-op when missing or already visible). */
  reveal(id: PrepTourAnchorId | undefined): void {
    const entry = id ? this.entries.get(id) : undefined;
    const el = entry ? entry.el : null;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const r = el.getBoundingClientRect();
    const inView = r.top >= 0 && r.bottom <= window.innerHeight && r.left >= 0 && r.right <= window.innerWidth;
    if (!inView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Start geometry observation; returns the stop function. Reference-counted. */
  watch(): () => void {
    if (this.watchers++ === 0) this.startObserving();
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      if (--this.watchers === 0) this.stopObserving();
    };
  }

  /** Force a geometry re-read (e.g. after a programmatic layout change). */
  invalidate(): void {
    this.schedule();
  }

  private startObserving(): void {
    if (typeof window === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.schedule());
        this.observer.observe(document.documentElement);
        this.entries.forEach((entry) => this.observer!.observe(entry.el));
      }
      window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    });
    this.schedule();
  }

  private stopObserving(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (typeof window === 'undefined') return;
    window.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  private schedule(): void {
    if (this.frame || typeof requestAnimationFrame !== 'function') {
      if (!this.frame) this.emit();
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.emit();
      });
    });
  }

  private emit(): void {
    this._changes.next();
  }
}
